import { PlayArrowSharp as PlayArrowSharpIcon } from '@mui/icons-material';
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { LoadingButton } from '@mui/lab';
import {
  Divider,
  IconButton,
  Input,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  Stack,
  Typography,
  alpha,
  styled,
} from '@mui/material';
import { m } from 'framer-motion';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EmojiPicker from 'src/components/EmojiPicker';
import UploadMediaComponent from 'src/components/upload-media/uploadMedia.component';
import { useToaster } from 'src/utils/toaster/toasterContext';
import { PostUtil } from '../new-post-new/post-util';
import HttpServices from 'src/services/httpService';
import ThreeDotLoader from 'src/utils/Loader/threeDotLoader.compontent';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useResellerLimitDialog } from 'src/components/reseller-limit-context/resellerLimitContext';

const RootStyle = styled('div')(({ theme }) => ({
  minHeight: 56,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

const InputArea = styled('div')(({ theme }) => ({
  minHeight: 56,
  display: 'flex',
  alignItems: 'center',
  width: '100%',
}));

export default function ChatMessageInput({
  disabled,
  conversationId,
  onSend,
  isSendLoading,
  fromLatestMessageDate,
  selectedConversation,
  facebookPageId,
}) {
  const fileInputRef = useRef(null);
  const textInputRef = useRef(null);
  const [message, setMessage] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [filePreview, setFilePreview] = useState('');
  const [type, setType] = useState('');
  const { showToast } = useToaster();
  const { t: translate } = useTranslation();
  const maxFileSizeInBytes = 25 * 1024 * 1024;
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiSuggestionAnchorEl, setAiSuggestionAnchorEl] = useState(null);
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
  const { showResellerLimitDialog } = useResellerLimitDialog();

  const getLatestMessageInfo = () => {
    if (!selectedConversation?.messages || selectedConversation.messages.length === 0) {
      return { text: '', isFromOther: false, isUnreplied: false };
    }

    const sortedMessages = [...selectedConversation.messages].sort(
      (a, b) => new Date(b.created_time) - new Date(a.created_time)
    );

    const latestOtherMessage = sortedMessages.find((msg) => msg.from.id !== facebookPageId && msg.text);

    if (!latestOtherMessage) {
      return { text: '', isFromOther: false, isUnreplied: false };
    }

    const hasReply = sortedMessages.some(
      (msg) => msg.from.id === facebookPageId && new Date(msg.created_time) > new Date(latestOtherMessage.created_time)
    );

    return {
      text: latestOtherMessage.text,
      isFromOther: true,
      isUnreplied: !hasReply,
    };
  };

  const { text: latestMessageText, isFromOther, isUnreplied } = getLatestMessageInfo();

  const handleAttach = () => {
    fileInputRef.current?.click();
  };

  const handleUploadMediaSuccessCallback = (mediaUrlList, type, file_size, aspect_ratio) => {
    if (aspect_ratio.width > 1440) {
      showToast(`Maximum width resolution is 1440px. Your media width is ${aspect_ratio.width}px.`, 'e');
      handleRemovePreview();
      return;
    }
    setFileUrl(mediaUrlList);
    setType(type);
  };

  const handleUploadMediaFailureCallback = (message) => {
    message && showToast(message, 'e');
    handleRemovePreview();
  };

  const handleClear = () => {
    setMessage('');
    setFileUrl('');
    setFilePreview('');
    setAiSuggestions([]);
    setAiSuggestionAnchorEl(null);
  };

  const handleSend = () => {
    if (!message && !fileUrl) {
      return '';
    }
    if (onSend && conversationId) {
      onSend({
        id: conversationId,
        text: message,
        imageUrl: fileUrl,
        type: type,
        latestTime: fromLatestMessageDate,
      });
    }
    return handleClear();
  };

  const handleEmoji = (emoji) => {
    setMessage(message + emoji);
  };

  const getFileExtension = (filename) => {
    return filename.split('.').pop().toLowerCase();
  };

  const handleFiles = (event) => {
    const files = event.currentTarget.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    if (file.size > maxFileSizeInBytes) {
      showToast(`File size must be less than 25 MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)} MB.`, 'e');
      return;
    }

    const unsupportedExtensions = ['jfif'];
    const extension = getFileExtension(file.name);
    if (!PostUtil.SupportedFileFormat.includes(file.type) || unsupportedExtensions.includes(extension)) {
      showToast('We only support JPEG, JPG, PNG & MP4 file formats.', 'e');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFilePreview(reader.result);
    };
    reader.readAsDataURL(file);

    UploadMediaComponent({
      files: [file],
      handleUploadMediaSuccessCallback,
      handleUploadMediaFailureCallback,
    });
  };

  const handleRemovePreview = () => {
    setFilePreview('');
    setFileUrl('');
    setType('');
    setAiSuggestions([]);
    setAiSuggestionAnchorEl(null);
  };
  const fetchAiSuggestions = () => {
    if (!isFromOther || !isUnreplied || fileUrl || disabled || !conversationId || !latestMessageText) {
      setAiSuggestions([]);
      setAiSuggestionAnchorEl(null);
      showToast('No AI suggestion data available', 'e');
      return;
    }
    setAiSuggestionAnchorEl(textInputRef.current);
    setIsFetchingSuggestions(true);
    const postProps = {
      url: userModuleURL.getAiSuggestionMessage,
      body: {
        question: latestMessageText,
        conversation_id: conversationId,
      },
      successCallback: (data, message) => {
        setIsFetchingSuggestions(false);
        if (data.suggestions && data.suggestions.length > 0) {
          setAiSuggestions(data.suggestions);
        } else {
          setAiSuggestions([]);
          setAiSuggestionAnchorEl(null);
          showToast('No AI suggestion data available', 'e');
        }
      },
      failureCallback: (message) => {
        setIsFetchingSuggestions(false);
        setAiSuggestions([]);
        setAiSuggestionAnchorEl(null);
        if (message && !showResellerLimitDialog(message)) {
          showToast(message, 'e');
        }
      },
    };
    HttpServices.Post(postProps);
  };

  const handleSelectAiSuggestion = (suggestion) => {
    setMessage(suggestion);
    setAiSuggestions([]);
    setAiSuggestionAnchorEl(null);
  };

  return (
    <RootStyle>
      {filePreview && (
        <ListItem
          component={m.div}
          sx={{
            p: 0,
            m: 0.5,
            width: 100,
            height: 100,
            borderRadius: 1.25,
            overflow: 'hidden',
            position: 'relative',
            display: 'inline-flex',
            opacity: 1,
            border: (theme) => `solid 1px ${theme.palette.divider}`,
          }}
        >
          {type === 'IMAGE' ? (
            <img src={fileUrl} alt={fileUrl} style={{ objectFit: 'cover', width: '100%', height: '100%' }} />
          ) : (
            <>
              <video src={fileUrl} alt={fileUrl} style={{ objectFit: 'cover', width: '100%', height: '100%' }} />
              <PlayArrowSharpIcon
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 1,
                  borderRadius: '50%',
                  background: 'black',
                  overflow: 'hidden',
                  color: 'white',
                }}
              />
            </>
          )}
          <IconButton
            size="small"
            onClick={() => handleRemovePreview()}
            sx={{
              top: 6,
              p: '2px',
              right: 6,
              position: 'absolute',
              color: 'common.white',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
              },
            }}
          >
            <CloseIcon sx={{ width: 20, height: 20 }} />
          </IconButton>
        </ListItem>
      )}

      <Menu
        anchorEl={aiSuggestionAnchorEl}
        open={Boolean(aiSuggestionAnchorEl)}
        onClose={() => {
          setAiSuggestionAnchorEl(null);
          setAiSuggestions([]);
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            maxWidth: 320,
            minWidth: 200,
            borderRadius: 2,
            boxShadow: (theme) => theme.shadows[4],
            backgroundColor: (theme) => theme.palette.background.paper,
            border: (theme) => `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
            maxHeight: 240,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: (theme) => theme.palette.grey[400],
              borderRadius: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: (theme) => theme.palette.background.paper,
            },
          },
        }}
      >
        {isFetchingSuggestions ? (
          <ListItem
            disableGutters
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body1" // Slightly larger text
              sx={{
                fontWeight: 600, // Increased weight
                color: (theme) => theme.palette.primary.main,
                letterSpacing: 0.5, // Improved readability
                mb: 0.25, // Reduced bottom margin (originally 0.5)
              }}
            >
        {translate('ai_message_comments.generating_replies')}
            </Typography>
            <ThreeDotLoader
              sx={{
                color: (theme) => theme.palette.primary.light, // Lighter color for loader
                fontSize: '1.5rem', // Larger loader
                animation: 'pulse 1.5s infinite', // Add subtle pulsing
              }}
            />
          </ListItem>
        ) : aiSuggestions.length > 0 ? (
          <List sx={{ p: 0, m: 0.5 }}>
            {aiSuggestions.map((suggestion, index) => (
              <ListItemButton
                key={index}
                onClick={() => handleSelectAiSuggestion(suggestion)}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: 1,
                  mx: 0.5,
                  transition: 'background-color 0.2s ease, transform 0.1s ease',
                  borderBottom:
                    index < aiSuggestions.length - 1
                      ? (theme) => `1px solid ${alpha(theme.palette.grey[300], 0.3)}`
                      : 'none',
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <ListItemText
                  primary={suggestion}
                  primaryTypographyProps={{
                    variant: 'body2',
                    sx: {
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      color: (theme) => theme.palette.text.primary,
                      fontWeight: 400,
                      lineHeight: 1.5,
                    },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        ) : (
          <ListItem
            sx={{
              px: 2,
              py: 2,
              justifyContent: 'center',
              bgcolor: (theme) => alpha(theme.palette.grey[100], 0.5),
              borderRadius: 1,
              mx: 1,
              my: 0.5,
            }}
          >
            <ListItemText
              primary={'No AI suggestions available'}
              primaryTypographyProps={{
                variant: 'body2',
                sx: {
                  color: (theme) => theme.palette.text.secondary,
                  fontStyle: 'italic',
                  textAlign: 'center',
                  fontWeight: 400,
                },
              }}
            />
          </ListItem>
        )}
      </Menu>

      <InputArea>
        <Input
          ref={textInputRef}
          disabled={fileUrl || disabled}
          fullWidth
          multiline
          minRows={1}
          maxRows={5}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder={translate('chat.type_a_message')}
          startAdornment={
            <InputAdornment position="start">
              <EmojiPicker handleEmoji={handleEmoji} />
            </InputAdornment>
          }
          endAdornment={
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0, mr: 1.5 }}>
              <IconButton disabled={message || fileUrl} size="small" onClick={handleAttach}>
                <AddPhotoAlternateRoundedIcon sx={{ height: '22px', width: '22px' }} />
              </IconButton>
              <IconButton
                disabled={fileUrl || disabled || !isFromOther || !isUnreplied || !latestMessageText}
                size="small"
                onClick={fetchAiSuggestions}
              >
                <AutoAwesomeIcon sx={{ height: '22px', width: '22px' }} />
              </IconButton>
            </Stack>
          }
        />

        <Divider orientation="vertical" flexItem />

        <LoadingButton
          variant="outlined"
          loading={isSendLoading}
          endIcon={<SendRoundedIcon />}
          size=""
          onClick={() => handleSend()}
          disabled={!message && !fileUrl}
          sx={{
            border: 'none',
            '&.Mui-disabled': {
              border: 'none',
              opacity: 0.6,
            },
            '&.MuiLoadingButton-root:hover': {
              border: 'none',
              background: 'none',
            },
          }}
        />
      </InputArea>

      <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFiles} />
    </RootStyle>
  );
}
